import { getActiveAppCode } from "@/config/config";
import {
	AuthLoginDocument,
	type AuthLoginMutation,
	AuthLoginVendorDocument,
	type AuthLoginVendorMutation,
	AuthRefreshTokenDocument,
	type AuthRefreshTokenMutation,
} from "@/generated/graphql";
import {
	type AuthProvider,
	type AuthSession,
	createAuthAdapter,
	createBrowserStorage,
} from "@k3-tech/react-kit";
import { urqlClient } from "../graphql/client";

// Define the session/user types if needed
export type K3User = unknown;
export type K3Role = string;
export type K3Permission = string;
export type K3Credentials = { username: string; password: string };

export type K3Session = AuthSession<K3User, K3Role, K3Permission>;

// Storage key for persisting session
const storage = createBrowserStorage<K3Session>({ key: "k3-auth" });

export const authAdapter = createAuthAdapter<
	K3User,
	K3Role,
	K3Permission,
	K3Session,
	K3Credentials
>({
	storage,
	// Optional: automatically refresh before expiry if your backend supports refresh
	autoRefresh: true,
	// Map how to resolve parts of session (defaults match our AuthSession shape)
	resolveUser: (session) => session?.user ?? null,
	resolveRoles: (session) => session?.roles?.filter(Boolean) ?? [],
	resolvePermissions: (session) => session?.permissions?.filter(Boolean) ?? [],
	// Implement GraphQL-based login and refresh based on schema.
	// Each portal has its own server-side mutation; the URL the user is on
	// determines which one we call. The BE rejects mismatched user_type
	// (internal must use authLogin, external must use authLoginVendor).
	login: async (credentials) => {
		const appCode = getActiveAppCode();
		const isVendor = appCode === "web_portal_vendor";
		const data = isVendor
			? await urqlClient
					.mutation<AuthLoginVendorMutation>(AuthLoginVendorDocument, {
						input: credentials,
					})
					.toPromise()
			: await urqlClient
					.mutation<AuthLoginMutation>(AuthLoginDocument, {
						input: credentials,
					})
					.toPromise();
		if (data.error) {
			throw data.error;
		}
		const payload = isVendor
			? (data.data as AuthLoginVendorMutation | undefined)?.authLoginVendor
			: (data.data as AuthLoginMutation | undefined)?.authLogin;
		if (!payload || !payload.accessToken || !payload.user) {
			throw new Error("Invalid login response");
		}

		const permissionIDs =
			payload?.appRolePermissions
				?.filter((x) => x.appID === getActiveAppCode())
				?.flatMap(
					(x) =>
						x.roles?.flatMap((y) =>
							y.permissions?.map((z) => z.permissionID),
						) ?? [],
				)
				?.filter((id: unknown): id is string => typeof id === "string") ?? [];
		return {
			user: payload.user as K3User,
			accessToken: payload.accessToken,
			refreshToken: payload.refreshToken,
			roles: [] as K3Role[],
			permissions: permissionIDs as K3Permission[],
			meta: {
				branches: payload.branches,
				isAllBranches: payload.isAllBranches,
				defaultBranchID: payload.defaultBranchID,
				defaultBranchCode: payload.defaultBranchCode,
				defaultBranchName: payload.defaultBranchName,
				defaultRoleName: payload.defaultRoleName,
				defaultWarehouseID: payload.defaultWarehouseID,
				defaultWarehouseName: payload.defaultWarehouseName,
			},
			// expiresAt is unknown from schema; leave undefined so autoRefresh stays off
		};
	},
	refresh: async (session) => {
		if (!session.refreshToken) return session;
		const data = await urqlClient
			.mutation<AuthRefreshTokenMutation>(
				AuthRefreshTokenDocument,
				{
					refreshToken: session.refreshToken,
				},
				{
					skipAuth: true,
				},
			)
			.toPromise();
		if (data.error) {
			// Check if the error is due to expired refresh token
			const errorMessage =
				data.error.graphQLErrors?.[0]?.message || data.error.message || "";
			const isTokenExpired =
				errorMessage.includes("token is expired") ||
				errorMessage.includes('exp" not satisfied');

			if (isTokenExpired) {
				// Clear all session data
				storage.clear();
				localStorage.clear(); // Clear all localStorage as a safety measure
				sessionStorage.clear(); // Also clear sessionStorage
				// Use replace() to forcefully reload the page at login
				window.location.replace("/login");
				// Return null to signal session is invalid
				// biome-ignore lint/suspicious/noExplicitAny: Required to signal session invalidation
				return null as any;
			}
			throw data.error;
		}
		const payload = data.data?.authRefreshToken;
		if (!payload) {
			throw new Error("Invalid refresh response");
		}
		const permissionIDs =
			payload?.appRolePermissions
				?.filter((x) => x.appID === getActiveAppCode())
				?.flatMap(
					(x) =>
						x.roles?.flatMap((y) =>
							y.permissions?.map((z) => z.permissionID),
						) ?? [],
				)
				?.filter((id: unknown): id is string => typeof id === "string") ?? [];
		return {
			...session,
			user: payload.user as K3User,
			accessToken: payload.accessToken,
			refreshToken: payload.refreshToken ?? session.refreshToken,
			roles: [] as K3Role[],
			permissions: permissionIDs as K3Permission[],
			meta: {
				branches: payload.branches,
				isAllBranches: payload.isAllBranches,
				defaultBranchID: payload.defaultBranchID,
				defaultBranchCode: payload.defaultBranchCode,
				defaultBranchName: payload.defaultBranchName,
				defaultRoleName: payload.defaultRoleName,
				defaultWarehouseID: payload.defaultWarehouseID,
				defaultWarehouseName: payload.defaultWarehouseName,
			},
		};
	},
	// Optional logout: backend didn't expose logout in schema; clear local state on adapter.logout
	logout: async (_session) => {
		// No-op server call; adapter will clear session
	},
	middleware: [
		{
			onSessionExpired: async () => {
				// Clear all session data
				storage.clear();
				localStorage.clear(); // Clear all localStorage as a safety measure
				sessionStorage.clear(); // Also clear sessionStorage
				// Use replace() to forcefully reload the page at login
				window.location.replace("/login");
			},
		},
	],
});

export type AuthProviderType = typeof AuthProvider;
